import { Control, FieldValues, useController } from "react-hook-form";
import { FormControlLabel, Checkbox } from "@mui/material";
import styled from "styled-components";
import { ReactRefI } from "type/common/ref.type";
import { ChangeEvent, useEffect } from "react";

type CheckboxFieldProps = {
  name: string;
  control: Control<FieldValues, any>;
  label: string;
  defaultValue?: boolean;
  valueRef?: ReactRefI<boolean>;
  onChange?: (value: boolean) => void;
  hideCheckbox?: boolean;
  className?: string;
};

const CheckboxField = ({
  name,
  control,
  label,
  defaultValue = false,
  valueRef,
  onChange,
  hideCheckbox = false,
  className,
}: CheckboxFieldProps) => {
  // ✅ Correct destructuring - get 'field' from useController
  const {
    field, // ✅ This contains value, onChange, onBlur, ref
    fieldState,
  } = useController({
    name,
    control,
    defaultValue,
  });

  const { value, onChange: fieldOnChange } = field;

  const isChecked = Boolean(value ?? defaultValue);

  useEffect(() => {
    if (valueRef) {
      valueRef.current = isChecked;
    }
    if (onChange) {
      onChange(isChecked);
    }
  }, [isChecked, onChange, valueRef]);

  const handleMuiChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    fieldOnChange(checked);
  };

  const handleNativeChange = (event: ChangeEvent<HTMLInputElement>) => {
    fieldOnChange(event.target.checked);
  };

  return (
    <StyledFormControlLabel
      className={className}
      control={
        hideCheckbox ? (
          <HiddenCheckbox
            checked={isChecked}
            onChange={handleNativeChange}
            type="checkbox"
          />
        ) : (
          <Checkbox checked={isChecked} onChange={handleMuiChange} />
        )
      }
      label={<LabelText isChecked={isChecked}>{label}</LabelText>}
    />
  );
};

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  & .MuiFormControlLabel-label {
    cursor: pointer;
    user-select: none;
  }
`;

const LabelText = styled.span<{ isChecked: boolean }>`
  font-weight: ${({ isChecked }) => (isChecked ? "bold" : "normal")};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

export default CheckboxField;
